import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { PickersUtilsProvider } from '@mui';
import Dialog from '@shared/dialogs/dialog';
import DialogActions from '@shared/dialogs/dialog-actions';
import MomentTimePicker from '@shared/moment-time-picker';

const TimeRangeDialog = ({
  defaultRange,
  onChange,
  onClose,
  open
}) => {
  const { start: defaultStart, end: defaultEnd } = defaultRange || {};

  const parseTime = useCallback(timeStr => {
    if (!timeStr) {
      return null;
    }
    return moment(timeStr, 'HH:mm');
  }, []);

  const [start, setStart] = useState(() => parseTime(defaultStart));
  const [end, setEnd] = useState(() => parseTime(defaultEnd));

  const resetToDefault = useCallback(() => {
    setStart(parseTime(defaultStart));
    setEnd(parseTime(defaultEnd));
  }, [defaultStart, defaultEnd, parseTime]);

  useEffect(() => {
    resetToDefault();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (!prevOpenRef.current && open) {
      resetToDefault();
    }
    prevOpenRef.current = open;
  }, [open, resetToDefault]);

  const handleCancel = useCallback(() => {
    resetToDefault();
    if (onClose) {
      onClose();
    }
  }, [resetToDefault, onClose]);

  const handleApply = useCallback(() => {
    if (onChange && start && end) {
      onChange({
        start: moment(start).format('HH:mm'),
        end: moment(end).format('HH:mm')
      });
    }
    if (onClose) {
      onClose();
    }
  }, [onChange, onClose, start, end]);

  const getActions = () => (
    <DialogActions
      onCancel={handleCancel}
      onSubmit={handleApply}
      submitLabel="APPLY"
    />
  );

  if (!open) {
    return null;
  }

  return (
    <Dialog
      actions={getActions()}
      maxWidth="26rem"
      onClose={handleCancel}
      title="Custom time range"
    >
      <PickersUtilsProvider>
        <MomentTimePicker
          floatingLabelText="From"
          fullWidth
          value={start}
          id="time_range_from"
          onChange={setStart}
        />
        <MomentTimePicker
          floatingLabelText="To"
          fullWidth
          value={end}
          id="time_range_to"
          onChange={setEnd}
        />
      </PickersUtilsProvider>
    </Dialog>
  );
};

TimeRangeDialog.propTypes = {
  defaultRange: PropTypes.object,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  open: PropTypes.bool
};

export default memo(TimeRangeDialog);
